/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, of, ReplaySubject } from 'rxjs';
import Amplify, { Auth } from 'aws-amplify';
import { Location } from '@angular/common';
import { User } from '../users/user.model';
import { select, Store } from '@ngrx/store';
import { environment } from '../../environments/environment';
import { CognitoUser } from '@aws-amplify/auth';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import * as fromLayout from '../layout/state/layout.actions';
import { ShowError, ShowInfo, ShowSuccess } from '../layout/state/layout.actions';
import * as fromRoot from '../app.reducer';
import { DoFirstLogin, SetAuthenticated, SetUnauthenticated } from './state/auth.actions';
import { LocationsService } from '../services/locations.service';
import { CognitoRefreshToken, CognitoUserPool } from 'amazon-cognito-identity-js';
import { RouterFacade } from '../router/router.facade';
import { CommonApiService } from '../shared/common-api.service';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { DeviceDetectionService } from '../services/device-detection.service';
import { cloneDeep } from 'lodash';
import { NetworkDetectService } from '../services/network-detect.service';
import { Capacitor } from '@capacitor/core';
import { SavePassword } from 'capacitor-ios-autofill-save-password';
import { StorageService } from '../player/player-storage/storage.service';
import { WebStorageService } from '../player/player-storage/web-storage.service';

enum AuthedUserRole {
  AccountManager = 'Account manager',
  LocationManager = 'Location manager',
  PlaybackManager = 'Playback manager',
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _cognito: BehaviorSubject<CognitoUser> = new BehaviorSubject(null);
  isDirectClient$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  cognito$ = this._cognito.asObservable();

  private httpClient: HttpClient;

  constructor(
    private routerFacade: RouterFacade,
    private store: Store<fromRoot.AppState>,
    private location: Location,
    private locationsService: LocationsService,
    private commonApiService: CommonApiService,
    private handler: HttpBackend,
    private deviceDetectionService: DeviceDetectionService,
    private networkStatus: NetworkDetectService,
    private storageService: StorageService,
    private webStorageService: WebStorageService
  ) {
    Amplify.configure(environment.amplifyConfig);
    this.httpClient = new HttpClient(this.handler);
  }

  public isAuthenticated(): Observable<boolean> {
    return from(Auth.currentAuthenticatedUser()).pipe(
      map(user => {
        this.checkRole(user);
        return true;
      }),
      catchError(() => {
        this.doUnauthentication();
        return of(false);
      }),
    );
  }

  public isUnauthenticated(): Observable<boolean> {
    return from(Auth.currentAuthenticatedUser()).pipe(
      map(user => {
        this.doAuthentication(user).catch(console.error);
        return false;
      }),
      catchError(() => {
        this.doUnauthentication(false);
        return of(true);
      }),
    );
  }

  signIn(user: User) {
    if (!this.networkStatus.getNetworkStatus()) {
      this.store.dispatch(ShowError({ message: 'There is no internet connection' }));
      return;
    }
    this.store.dispatch(fromLayout.StartLoading(null));
    Auth.signIn(user.email, user.password)
      .then(returnedUser => {
        if (returnedUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
          this.doForceChangePassword(returnedUser);
        } else {
          if (Capacitor.getPlatform() === 'ios') {
            SavePassword.promptDialog({
                username: user.email,
                password: user.password
            });
          }
          this.doAuthentication(returnedUser,true,user.remember).catch(console.error);
        }
      })
      .catch(error => {
        this.doUnauthentication();
      })
      .finally(() => this.store.dispatch(fromLayout.StopLoading()));
  }

  completeNewPassword(user: CognitoUser, newPassword: string) {
    this.store.dispatch(fromLayout.StartLoading(null));
    Auth.completeNewPassword(user, newPassword, {})
      .then(returnedUser => {
        this.doAuthentication(returnedUser).catch(console.error);
      })
      .catch(() => this.doUnauthentication())
      .finally(() => this.store.dispatch(fromLayout.StopLoading()));
  }

  requestForgotPassword(username: string) {
    Auth.forgotPassword(username)
      .then(() => {
        const message = 'An e-mail was sent to your e-mail account.';
        this.store.dispatch(ShowSuccess({ message }));
      })
      .catch(err => this.store.dispatch(ShowError({ message: err.message })));
  }

  submitForgotPassword(username: string, code: string, password: string) {
    Auth.forgotPasswordSubmit(username, code, password)
      .then(() => {
        const message = 'Password changed. You can now log into with your new credentials.';
        this.store.dispatch(ShowSuccess({ message }));
        this.routerFacade.go(['/auth', 'login']);
      })
      .catch(err => this.store.dispatch(ShowError({ message: err.message })));
  }

  logout() {
    this.doLogout();
  }

  private doLogout() {
    this.forgetDevice().then(()=>{
      from(Auth.signOut())
      .pipe(take(1))
      .subscribe(
        next => this.doUnauthentication(),
        error => console.log(error),
      );
    });
  }

  setCognito(user: CognitoUser) {
    this._cognito.next(user);
  }

  getCognito(): CognitoUser {
    return this._cognito.getValue();
  }

  getToken(): string {
    const user = this.getCognito();
    return user && user.getSignInUserSession
      ? user
          .getSignInUserSession()
          .getIdToken()
          .getJwtToken()
      : null;
  }

  private async doAuthentication(user: CognitoUser, redirect = true, remember = false) {
    const currentSession = await Auth.currentSession();
    console.log('[Do Authentication] Current refresh token: ', currentSession.getRefreshToken());
    const cognito: CognitoUser = cloneDeep(user);
    this.store.dispatch(SetAuthenticated());
    this._cognito.next(cognito);
    if (this.isDirectClient$.value || localStorage.getItem('isDirectClient') === '1') {
      localStorage.setItem('isDirectClient', '1');
    }
    if (redirect) {
      const message = 'Login sucess';
      if(localStorage.getItem('isDirectClient') !== '1') {
        this.store.dispatch(ShowSuccess({ message }));
      }
      if(remember) {
        this.rememberDevice(user.getUsername());
      }
      this.checkRedirection().then(() => console.log('[Auth Service] Redirection called'));
    }
  }

  doRefreshToken(): Observable<CognitoUser | unknown> {
    return this.store.pipe(
      take(1),
      switchMap(() => {
        const user = this.getCognito();
        return from(Auth.currentSession()).pipe(map(currentSession => [user, currentSession]));
      }),
      switchMap(response => {
        const user = response[0] as CognitoUser;
        const currentSession = response[1] as any;
        return from(
          new Promise((resolve, reject) => {
            if (user.refreshSession) {
              user.refreshSession(currentSession.getRefreshToken(), (err, session) => {
                if (err) {
                  console.log('[Auth Service] Token refresh error: ', err);
                  this.isDirectClient$.next(false);
                  localStorage.setItem('isDirectClient', '0');
                  return reject(err);
                }
                const { idToken } = session;
                console.log('[Auth Service] New token: ', idToken.jwtToken);
                // @ts-ignore
                return resolve(user);
              });
            } else {
              return resolve(user);
            }
          }),
        );
      }),
    );
  }

  outdoorRefresh(refreshToken: string, email: string): Observable<CognitoUser | unknown> {
    const userPool = new CognitoUserPool({
      UserPoolId: environment.amplifyConfig.Auth.userPoolId,
      ClientId: environment.amplifyConfig.Auth.userPoolWebClientId,
    });

    const userData = {
      Username: email,
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);
    const refToken = new CognitoRefreshToken({ RefreshToken: refreshToken });

    return from(
      new Promise((resolve, reject) => {
        cognitoUser.refreshSession(refToken, (err, session) => {
          if (err) {
            console.log('[Auth Service] Token refresh error: ', err);
            return reject(err);
          }
          const { idToken } = session;
          console.log('[Auth Service] New token: ', idToken.jwtToken);
          // @ts-ignore
          return resolve(cognitoUser);
        });
      }),
    );
  }

  pairing(macAddress: string) {
    const payload = {
      macAddress,
    };
    const headers = new HttpHeaders({
      'x-api-key': environment.apiKey,
    });
    return this.httpClient
      .post(this.commonApiService.getUrl('device/pairing'), payload, { headers })
      .pipe(catchError(this.commonApiService.handleError));
  }

  matching(matchingObject: unknown) {
    const headers = new HttpHeaders({
      'x-api-key': environment.apiKey,
    });
    return this.httpClient
      .post(this.commonApiService.getUrl('device/matching'), matchingObject, { headers })
      .pipe(catchError(this.commonApiService.handleError));
  }

  unpair(macAddress: string) {
    const headers = new HttpHeaders({
      'x-api-key': environment.apiKey,
    });
    return this.commonApiService
      .getHttpClient()
      .delete(this.commonApiService.getUrl(`device/${macAddress.split(':').join('')}`), { headers })
      .pipe(catchError(this.commonApiService.handleError));
  }

  private async checkRedirection() {
    const locations = this.locationsService.getLocations().pipe(
      map(stations => {
        stations.map(station => {
          station.cover = station.image;
        });
        return stations;
      }),
    );
    locations.subscribe(result => {
      if (result.length === 1) {
        this.routerFacade.go(['/player', result[0].guid]);
      } else {
        this.routerFacade.go(['/locations']);
      }
    });
  }

  private doUnauthentication(redirect = true) {
    this.store.dispatch(SetUnauthenticated());
    this.isDirectClient$.next(false);
    localStorage.setItem('isDirectClient', '0');
    if (this.location.path().includes('/auth')) {
      redirect = false;
    }
    if (redirect) {
      this.routerFacade.go(['/auth', 'login']);
    }
  }

  private doForceChangePassword(user: CognitoUser) {
    this.setCognito(user);
    this.store.dispatch(DoFirstLogin());
  }

  private checkRole(user: any) {
    const attributes = user.challengeParam ? user.challengeParam.userAttributes : user.attributes;
    const customRole = attributes['custom:role'];

    if (
      Object.values(AuthedUserRole)
        .map(authedUserRole => authedUserRole.toLowerCase())
        .includes(customRole.toLowerCase())
    ) {
      this.doAuthentication(user, false).catch(console.error);
    } else {
      this.store.dispatch(ShowError({ message: 'Invalid user' }));
      this.logout();
    }
  }

  async rememberDevice(username: string) {
    try {
      // eslint-disable-next-line max-len
      const storageService = !Capacitor.isNativePlatform() && Capacitor.getPlatform()==='web' ? this.webStorageService : this.storageService;

      // const result = await Auth.rememberDevice();
      storageService.saveItem(username, 'savedUser');
      // console.log('rememberDevice', result);
    } catch (error) {
      console.log('Error remembering device', error);
    }
  }

  async forgetDevice() {
    try {
      // const result = await Auth.forgetDevice();
      console.log('forgetDevice');
    } catch (error) {
      console.log('Error forgetting device', error);
    }
  }

}
